import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { HttpParams } from '@angular/common/http';
import { ClinicItemJSON } from '../common/models/server-response/clinic-item-json.model';
import { ENTOfficeItemJSON } from '../common/models/server-response/ent-office-item-json.model';
import { RequestedOfficeHistoryItemJSON } from '../common/models/server-response/requested-office-history-item-json.model';
import { RequestedClinicHistoryItemJSON } from '../common/models/server-response/requested-clinic-history-item-json.model';
import { ShopTypesServerSideEnum } from '../common/enums/shop-types-server-side-enum';
import { LookUpTablesTimestampsJSON } from '../common/models/server-response/look-up-tables-timestamps-json.model';
import { AdminDBTimestampsJSON } from '../common/models/server-response/admin-db-timestamps-json';
import { CountryId } from 'src/common-panel/constants/country-configs.constants';

@Injectable({
  providedIn: 'root'
})
export class RequestsService
{

  constructor(private apiService: ApiService) { }

  getClinicList(country: CountryId): Observable<ClinicItemJSON[]>
  {
    const params = new HttpParams().set('country', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/shops`, params);
  }

  getOfficeList(country: CountryId): Observable<ENTOfficeItemJSON[]>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/offices`, params);
  }

  getClinicRequestsList(country: CountryId): Observable<RequestedClinicHistoryItemJSON[]>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/shopsRequests`, params);
  }

  getOfficeRequestsList(country: CountryId): Observable<RequestedOfficeHistoryItemJSON[]>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/officeRequests`, params);
  }

  createClinic(clinic: ClinicItemJSON, country: CountryId): Observable<any>
  {
    const params = new HttpParams().set('countryId', country).set('bBlockIfExists', true);
    return this.apiService.post(`/api/v1/wr/shopadmin/upsertShopItem`, clinic, params);
  }

  createOffice(office: ENTOfficeItemJSON, country: CountryId): Observable<any>
  {
    const params = new HttpParams().set('countryId', country).set('bBlockIfExists', true);
    return this.apiService.post(`/api/v1/wr/shopadmin/upsertENTItem`, office, params);
  }

  editClinic(clinic: ClinicItemJSON, country: CountryId): Observable<any>
  {
    const params = new HttpParams().set('countryId', country).set('bBlockIfExists', false);
    return this.apiService.post(`/api/v1/wr/shopadmin/upsertShopItem`, clinic, params);
  }

  editOffice(office: ENTOfficeItemJSON, country: CountryId): Observable<any>
  {
    const params = new HttpParams().set('countryId', country).set('bBlockIfExists', false);
    return this.apiService.post(`/api/v1/wr/shopadmin/upsertENTItem`, office, params);
  }

  deleteClinic(shopId: string): Observable<any>
  {
    const params = new HttpParams().set('shopId', shopId).set('bDeleteFromAdminDb', true);
    return this.apiService.delete(`/api/v1/wr/shopadmin/deleteShopItem`, params);
  }

  deleteOffice(officeId: string): Observable<any>
  {
    const params = new HttpParams().set('entId', officeId).set('bDeleteFromAdminDb', true);
    return this.apiService.delete(`/api/v1/wr/shopadmin/deleteENTItem`, params);
  }

  rejectClinic(shopId: string): Observable<any>
  {
    const params = new HttpParams().set('shopId', shopId).set('bDeleteFromAdminDb', false);
    return this.apiService.delete(`/api/v1/wr/shopadmin/deleteShopItem`, params);
  }

  rejectOffice(officeId: string): Observable<any>
  {
    const params = new HttpParams().set('entId', officeId).set('bDeleteFromAdminDb', false);
    return this.apiService.delete(`/api/v1/wr/shopadmin/deleteENTItem`, params);
  }

  approveClinic(shopId: string): Observable<any>
  {
    const params = new HttpParams().set('shopId', shopId).set('bUpdateInAdminDb', true);
    return this.apiService.post(`/api/v1/wr/shopadmin/approveShopItem`, {}, params);
  }

  approveOffice(officeId: string): Observable<any>
  {
    const params = new HttpParams().set('officeId', officeId).set('bUpdateInAdminDb', true);
    return this.apiService.post(`/api/v1/wr/shopadmin/approveENTItem`, {}, params);
  }

  createShopRequestFromExistingShop(shopId: string, countryId: CountryId): Observable<RequestedClinicHistoryItemJSON>
  {
    const params = new HttpParams().set('shopId', shopId).set('countryId', countryId);
    return this.apiService.get(`/api/v1/wr/shopadmin/createShopRequestFromExistingShop`, params);
  }

  createEntRequestFromExistingEnt(entId: string, countryId: CountryId): Observable<RequestedOfficeHistoryItemJSON>
  {
    const params = new HttpParams().set('entId', entId).set('countryId', countryId);
    return this.apiService.get(`/api/v1/wr/shopadmin/createEntRequestFromExistingEnt`, params);
  }

  getItemLog(shopId: string, type: ShopTypesServerSideEnum): Observable<any>
  {
    const params = new HttpParams().set('itemId', shopId).set('type', type);
    return this.apiService.get(`/api/v1/wr/shopadmin/getItemById`, params);
  }

  getCountryHCPCount(country: CountryId): Observable<{ [id: string]: number }>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/countryHCPCount`, params);
  }

  getCountryClinicPatientCount(country: CountryId): Observable<{ [id: string]: number }>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/countryPatientCount`, params);
  }

  getCountryOfficePatientCount(country: CountryId): Observable<{ [id: string]: number }>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/countryENTPatientCount`, params);
  }

  getCountryENTCount(country: CountryId): Observable<{ [id: string]: number }>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.get(`/api/v1/wr/shopadmin/countryENTCount`, params);
  }

  getShopsCSV(country: CountryId): Observable<any>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.getCSV(`/api/v1/wr/shopadmin/shopsCSV`, params);
  }

  getOfficesCSV(country: CountryId): Observable<any>
  {
    const params = new HttpParams().set('countryId', country);
    return this.apiService.getCSV(`/api/v1/wr/shopadmin/officesCSV`, params);
  }

  generateZipLookupTable(countryId: string): Observable<any>
  {
    const params = new HttpParams().set('countryId', countryId);
    return this.apiService.get(`/api/v1/wr/admin/generateShopIdZipCodeRelationByCountryJSON`, params);
  }

  getTimestampsZipCodeRelation(): Observable<LookUpTablesTimestampsJSON>
  {
    return this.apiService.get(`/api/v1/wr/admin/getTimestampsZipCodeRelationJSON`);
  }

  getTimestampsFromAdminDb(): Observable<AdminDBTimestampsJSON[]>
  {
    return this.apiService.get(`/api/v1/wr/admin/getTimestampsFromAdminDb`);
  }

}
